/**
 * foundOrganization — RPC contract for the callable a signed-in person with
 * a listed domain calls to found an organization.
 *
 * Cross-process contract between the founding step (caller) and
 * `firebase/src/auth/foundOrganization.api.ts` (onCall handler).
 * Both ends parse with `.strict()` — an extra key fails the whole request.
 *
 * The caller is the authenticated account; its address is read from the
 * auth token, never from the request. It may found when its address's
 * domain is listed in `platform/founding`, once: the founding names the
 * founder's uid as `foundedBy` on the organization's private settings, which
 * no client may write, and a uid an organization names there has founded.
 *
 * Fields justified:
 *   name — the organization's display name; the founded card's brand name
 *          and the source of its derived id.
 *
 * Output:
 *   orgId — the founded organization; it is saved as the caller's active
 *           organization and the caller opens it.
 *   name  — the founded organization's name as stored.
 *
 * A repeat call whose `name` equals the founded organization's answers that
 * organization and writes nothing, so a retried call whose answer was lost
 * lands where the first did.
 *
 * Excluded:
 *   email — the auth token carries the caller's address; a request field
 *           would let one account found for another.
 *   brand — logo and colours are the card's own fields, edited after
 *           founding where they are edited today.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   unauthenticated — no signed-in caller.
 *   permission-denied / founding-not-admitted — the address's domain is not
 *     listed, or the session's address is not inbox-proven.
 *   failed-precondition / already-founded — the address has founded an
 *     organization under a different name.
 *   failed-precondition / founding-unavailable — no `platform/mail`, or no
 *     free organization id; nothing is founded.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const OrganizationNameSchema = z.string().trim().min(1);

export const FoundOrganizationInput = z
	.object({
		name: OrganizationNameSchema.describe("the organization's display name"),
	})
	.strict();
export type FoundOrganizationInput = z.infer<typeof FoundOrganizationInput>;

export const FoundOrganizationOutput = z
	.object({
		orgId: Id.describe("the organization the caller founded"),
		name: OrganizationNameSchema.describe(
			"the founded organization's name as stored",
		),
	})
	.strict();
export type FoundOrganizationOutput = z.infer<typeof FoundOrganizationOutput>;
